import { Box, DialogActions } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import {
  CarrierType,
  CreateGeneralCargoOrderCommand,
  SealType,
  UpsertOrderListCommandV2,
  UpsertRestowListCommand,
} from '@planning/app/api'
import { LoaderButton } from '@planning/components/LoaderButton'
import { OrderListUploadViewStoreV2 } from '@planning/pages/Order/stores/OrderListUploadViewStoreV2'
import { carrierVisitService } from '@planning/services'
import generalCargoService from '@planning/services/generalCargoService'
import restowService from '@planning/services/restowService'
import { useTranslate } from '@tolgee/react'
import { AxiosError } from 'axios'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import { UseFormReset } from 'react-hook-form'
import { useOrderListUploadMessagesHandler } from './hooks/useOrderListUploadMessagesHandler'

export interface IProps {
  store: OrderListUploadViewStoreV2
  resetForm: UseFormReset<any>
  isRestow?: boolean
}

export type ErrorResponse = {
  detail: string
}

export const handleRestowUnitNotFoundError = (error: AxiosError<ErrorResponse>): string[] => {
  const detail = error.response?.data.detail
  if (error.response?.status === 404 && detail) {
    // match the pattern "Entity "RemainOnBoardUnit" (MBIU0415643, MBIU0415642, MBIU0415641) was not found."
    // do not use regex here because of super-linear runtime due to backtracking problem with large input
    const start = detail.indexOf('(')
    const end = detail.indexOf(')', start)
    const missingedContainerNumbers = start > -1 && end > -1 ? detail.substring(start + 1, end) : ''
    return missingedContainerNumbers ? missingedContainerNumbers.split(',').map(m => m.trim()) : []
  }

  return []
}

export const OrderListUploadDialogFooter: FC<IProps> = observer(
  ({ store, isRestow, resetForm }) => {
    const { appViewStore, orderListUploadViewStoreV2, drawerStore, containerJourneyItemStore } =
      usePlanningStore()

    const { t } = useTranslate()

    const { getExceptionMessage } = useOrderListUploadMessagesHandler()

    const validateInput = (): boolean => {
      if (!store.orderUpdates && !store.isGeneralCargoVisit) return false
      if (store.isGeneralCargoVisit && store.generalCargoUploadOrders.length === 0) return false

      if (!store.vesselVisitId && !store.railVisitId) {
        appViewStore.setShowAlert('error', t('missingVisitId', 'Missing visit id'))
        return false
      }

      if (!store.customer && !store.isGeneralCargoVisit) {
        appViewStore.setShowAlert('error', t('missingCustomer', 'Missing customer'))
        return false
      }

      return true
    }

    const upsertRestowList = async () => {
      try {
        appViewStore.isLoading = true

        const restowCmd: UpsertRestowListCommand = {
          vesselVisitId: store.vesselVisitId ?? 0,
          customerId: store.customer?.id ?? 0,
          restows: store.dataStore.restowUnits ?? [],
        }

        await restowService.upsertRestowList(restowCmd)

        appViewStore.setShowAlert(
          'success',
          t('fileUploadedSuccessfully', 'File uploaded successfully'),
        )
        finalizeProcess()
      } catch (error) {
        if (isRestow && error instanceof AxiosError) {
          const unitMessage = handleRestowUnitNotFoundError(error)

          if (unitMessage) {
            orderListUploadViewStoreV2.setActiveStep(0)
            orderListUploadViewStoreV2.setUploadFailedReason(
              `${t('unitCannotBeRestowed', 'Unit cannot be restowed')}: ${unitMessage.join(', ')} ${unitMessage.length === 1 ? t('wasNotFound', 'was not found').toLocaleLowerCase() : t('wereNotFound', 'were not found').toLocaleLowerCase()}. 
              ${t('containersNoLongerInBaplieOrInDischarge', 'These containers are either no longer included in the BAPLIE or have now been announced as discharge')}.`,
            )
            return
          } else {
            appViewStore.setShowAlert('error', t('failToUploadFile', 'Fail to upload file'))
          }
        } else {
          appViewStore.setShowAlert('error', t('failToUploadFile', 'Fail to upload file'))
        }
      } finally {
        appViewStore.isLoading = false
      }
    }

    const upsertGeneralCargoOrderList = async () => {
      try {
        appViewStore.isLoading = true

        const carrierVisitType = store.vesselVisitId ? CarrierType.Vessel : CarrierType.Train
        const carrierVisitId = store.vesselVisitId ?? store.railVisitId ?? 0

        const commands: CreateGeneralCargoOrderCommand[] = store.generalCargoUploadOrders.map(
          order =>
            ({
              referenceNumber: order.referenceNumber,
              direction: store.direction,
              imoClasses: order.imoClasses,
              carrierVisitType,
              carrierVisitId,
              customerId: store.customer?.id,
              lotNumber: order.lotNumber,
              commodityId: order.commodityId,
              plannedAmount: order.plannedAmount,
              identifiableItems: order.identifiableItems,
              grossWeight: order.grossWeight,
              weightUnit: order.weightUnit,
              railTrackId: order.railTrackId,
              railcarId: order.railcarId,
            }) as CreateGeneralCargoOrderCommand,
        )

        await generalCargoService.createBulk(commands)

        appViewStore.setShowAlert(
          'success',
          t('fileUploadedSuccessfully', 'File uploaded successfully'),
        )
        finalizeProcess()
      } catch (error) {
        if (error instanceof AxiosError && error.response?.data?.detail) {
          appViewStore.setShowAlert('error', error.response?.data?.detail)
        } else {
          appViewStore.setShowAlert('error', t('failToUploadFile', 'Fail to upload file'))
        }
      } finally {
        appViewStore.isLoading = false
      }
    }

    const upsertOrderList = async () => {
      try {
        appViewStore.isLoading = true

        const ordersToAdd = store.orderUpdates?.created.filter(
          o => store.acceptUpdates[o.containerNumber],
        )

        const cmd: UpsertOrderListCommandV2 = {
          listName: store.listName,
          carrierVisitId: store.vesselVisitId ?? store.railVisitId ?? 0,
          direction: store.direction,
          customerId: store.customer?.id ?? 0,
          addOrders: ordersToAdd?.map(order => ({
            ...order,
            hasSeals: !!order.seals,
            seals: order.seals
              ? order.seals.map(seal => ({
                  ...seal,
                  type: seal.type ?? SealType.Unknown,
                }))
              : [],
          })),
          addEmptyRailcars: store.railVisitId
            ? store.orderUpdates?.created.filter(o => !store.acceptUpdates[o.containerNumber])
            : [],
          addRemainOnBoard: store.dataStore.remainOnBoard,
          addNonNumeric: store.dataStore.nonNumeric,
          updateOrders: store.orderUpdates?.amended
            .filter(o => store.acceptUpdates[o.containerNumber ?? ''])
            .map(amendment => amendment.update),
          deleteOrders: store.orderUpdates?.deleted
            .filter(o => store.acceptUpdates[o.containerNumber ?? ''])
            .map(o => o.id),
        }

        await carrierVisitService.upsertOrderListV2(cmd)

        appViewStore.setShowAlert(
          'success',
          t('fileUploadedSuccessfully', 'File uploaded successfully'),
        )

        // fetch container journeys for newly created orders
        const addedContainerNumbers = ordersToAdd?.map(o => o.containerNumber)
        if (addedContainerNumbers?.length)
          containerJourneyItemStore.fetchByContainerNumbers(addedContainerNumbers)

        finalizeProcess()
      } catch (error) {
        appViewStore.setShowAlert('error', getExceptionMessage(error))
      } finally {
        appViewStore.isLoading = false
      }
    }

    const finalizeProcess = () => {
      store.reset()
      resetForm()
      drawerStore.close()
    }

    const upsertOrderOrRestowList = async () => {
      if (!validateInput()) return

      if (isRestow) {
        await upsertRestowList()
      } else if (store.isGeneralCargoVisit) {
        await upsertGeneralCargoOrderList()
      } else {
        await upsertOrderList()
      }
    }

    const actions = () => {
      if (store.activeStep === 1)
        return (
          <Box data-cy='upload-documents-confirm-updates-btn'>
            <LoaderButton
              onClick={async () => await upsertOrderOrRestowList()}
              color='primary'
              variant='contained'
            >
              {t('confirmUpdates', 'Confirm updates')}
            </LoaderButton>
          </Box>
        )

      return (
        <Box data-cy='upload-documents-next-btn'>
          <LoaderButton color='primary' variant='contained' type='submit'>
            {t('next', 'Next')}
          </LoaderButton>
        </Box>
      )
    }

    return (
      <DialogActions
        sx={{
          width: '100%',
          p: '1rem 2rem!important',
          borderTop: '1px solid rgba(145, 158, 171, 0.24)',
        }}
      >
        <Box display='flex' alignItems='center' justifyContent='space-between' width='100%'>
          {actions()}
        </Box>
      </DialogActions>
    )
  },
)
